import type { DetailedCreditCard } from '@contracts/payment/CreditCard';
import type { TransactionCurrency } from '@contracts/payment/Currency';
import { styled } from '@mui/system';
import { CreditCardLogo } from '@src/clients/financialSettings/CreditCardLogo';
import type { CurrencyField } from '@src/clients/financialSettings/fields/CurrencyFields';
import { FormSelect } from '@src/common/components/FormSelect';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { CreditCardField } from '@src/orders/fields/CreditCardRelatedFields';
import type { Control, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type CreditCardFieldValue = FieldValues & {
  [CurrencyField.fieldName]: keyof typeof TransactionCurrency;
};
interface Props<T extends CreditCardFieldValue> {
  control: Control<T>;
  creditCards: DetailedCreditCard[];
  disabled?: boolean;
}

export const CreditCardSelector = <T extends CreditCardFieldValue>({
  control,
  creditCards,
  disabled,
}: Props<T>) => {
  const { t } = useTranslation();

  const CreditCardOption = ({
    creditCard,
  }: {
    creditCard: DetailedCreditCard;
  }) => (
    <Stack flex={1} direction="row" justifyContent="space-between">
      <Stack direction="row" gap={theme.spacing['1']} alignItems="center">
        <CreditCardLogo brand={creditCard.brand} />
        <Bold>{creditCard.cardName}</Bold>
        {creditCard.nameOnCard}
      </Stack>
      {creditCard.currency}
    </Stack>
  );

  return (
    <FormSelect
      label={t(`fields.${CreditCardField.fieldName}`)}
      fieldName={CreditCardField.fieldName}
      options={creditCards.map((card) => ({
        value: card.id,
        label: <CreditCardOption creditCard={card} />,
      }))}
      control={control as Control<CreditCardFieldValue>}
      disabled={disabled}
    />
  );
};

const Bold = styled('div')`
  font-weight: 500;
  width: 90px;
`;
